import { Navigate, useNavigate } from "react-router-dom";
import LoginLayout from "../../layouts/login.layout";
import { loginDefaultBanner } from "../../utils/constant/login.constant";
import ContainerHeader from "../../common/ui/ContainerHeader";
import { Image } from "../../common/ui/Image";
import OTPInputBox from "../../common/ui/otp/OTPInputBox";
import ResendOTP from "../../common/ui/otp/ResendOTP";
import { useState } from "react";
import TimeButton from "../../common/ui/time/TimeButton";
import { extractDeviceInfo } from "../../utils/helper/browser";
import { useSelector } from "react-redux";
import { loginSelector } from "../../store/features/loginSlice";
import { publicResendCode, validateMFA } from "../../services/public.services";
import { toastMessage } from "../../utils/helper/myToast";
import { useAppDispatch } from "@/store/store";
import { loginSuccessAction } from "@/store/actions/login.actions";
import { Button as CustomButton } from "@/components/ui/button";
import Button from "@/common/ui/Button";
import { motion } from "framer-motion";
import CustomLink from "@/common/ui/CustomLink";
import useClearHistoryAndNavigate from "@/hooks/useClearHistoryandNavigate";

const VIEW_SCREEN = {
  VERIFY_SUCCESS: "VERIFY_SUCCESS",
  TRY_OTHER_OPTIONS: "TRY_OTHER_OPTIONS",
};
interface VIEW {
  value: keyof typeof VIEW_SCREEN | "";
  data?: any;
}

const MfaValidatePage = () => {
  const [view, setView] = useState<VIEW>({ value: "", data: null });
  const [otp, setOtp] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [resetCount, setResetCount] = useState(0);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const clearNavigate = useClearHistoryAndNavigate();
  const dispatch = useAppDispatch();
  const { mfaValidateDetails } = useSelector(loginSelector);
  const isAuthenticatorApp = mfaValidateDetails?.mfaType === "AUTHENTICATOR";

  if (!mfaValidateDetails) {
    return <Navigate to="/login" replace />;
  }
  // Todo:Redirect if token not set
  const handleValidateMfa = async () => {
    setIsLoading(true);
    const browserInfo = await extractDeviceInfo();
    const payload: any = {
      browserInfo,
      isTrusted: rememberMe,
      totp: otp,
      token: mfaValidateDetails?.token,
      mfaType: mfaValidateDetails?.mfaType,
    };
    try {
      const res: any = await validateMFA(payload);

      if (res?.status === 200) {
        const { message, isError, data, showMessage } = res.data;
        setView({ value: "VERIFY_SUCCESS", data });
        return showMessage && toastMessage(message, isError);
      } else if (res?.response?.status === 401) {
        const { message } = res?.response?.data || {};
        setError(message);
        return;
      } else {
        console.log("Error", res);
        const { message } = res?.response?.data || {};
        if (message) {
          setError(message);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSMSResend = async () => publicResendCode({ token: mfaValidateDetails?.token }, "?key=sms-mfa");

  const gotoDashboard = () => {
    if (view.data) dispatch(loginSuccessAction(view.data));
    clearNavigate("/dashboard");
  };

  const renderContent = () => {
    switch (view.value) {
      case VIEW_SCREEN.VERIFY_SUCCESS:
        return (
          <>
            <ContainerHeader headingClassName=" text-base md:text-xl !leading-normal font-black text-text-label" heading="2 Factor Authentication Successful" />
            <Image src="/images/done.svg" alt="email-send-image" className=" hover:cursor-pointer" />
            <TimeButton handleClick={gotoDashboard} btnText="Take me to dashboard" text="You will be automatically re-directed to the Lystproof dashboard" />
          </>
        );
      case VIEW_SCREEN.TRY_OTHER_OPTIONS:
        return (
          <>
            <ContainerHeader
              handleBack={() => setView({ value: "" })}
              headingClassName="text-base md:text-xl !leading-normal font-black text-text-label"
              heading="Try other options"
            />
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`text-13 md:text-base !leading-normal w-full text-start text-text-light2 mt-2 px-3 py-5`}
            >
              Having trouble with verification? Reach out to our
              <CustomLink link={{ text: "support team", value: "mailto:support@lystproof.com" }} className="font-normal" />
              for assistance.
            </motion.p>
          </>
        );
      default:
        return (
          <>
            <ContainerHeader
              handleBack={() => navigate(-1)}
              headingClassName="text-base md:text-xl !leading-normal font-black text-text-label mx-2 md:mx-0"
              heading="Two factor authentication"
            />
            <Image src="/images/otp-sent.svg" alt="otp-send-image" className=" hover:cursor-pointer" />
            <p className=" text-center py-5 text-text-label text-13 md:text-base px-5 font-normal">
              {isAuthenticatorApp ? (
                <span>Enter the code from your authenticator app to log in.</span>
              ) : (
                <span>We have sent a code to {mfaValidateDetails?.identity}.</span>
              )}
              <br />
              Please enter it below.
            </p>
            <OTPInputBox setCompleteOtp={setOtp} resetCount={resetCount} errorMessage={error} resetErrorMessage={() => setError("")} />
            <div className=" flex flex-col w-full md:px-14 justify-start">
              {!isAuthenticatorApp && (
                <ResendOTP
                  resendOtpAPI={handleMfaSMSResend}
                  className="md:px-4"
                  errorMessage={error}
                  resetErrorMessage={() => setError("")}
                  setResetCount={setResetCount}
                />
              )}
              {/* Remember me */}
              <div className="flex items-center gap-2 py-3 md:py-2 w-full px-4">
                <div className="checkbox-wrapper-23" onClick={() => setRememberMe((pre) => !pre)}>
                  <input id={`remember-check`} checked={rememberMe} type="checkbox" onChange={() => setRememberMe((pre) => !pre)} />
                  <label htmlFor={`remember-check`}>
                    <svg viewBox="0,0,50,50" style={{ pointerEvents: "all", cursor: "pointer" }}>
                      <path d="M5 30 L 20 45 L 45 5"></path>
                    </svg>
                  </label>
                </div>
                <span className="text-text-default text-[12px] md:text-sm select-none">Remember me for 30 days in this browser.</span>
              </div>
            </div>
            <Button variant="primary" size="small" className=" my-5" isFullWidth disabled={!otp} onClick={handleValidateMfa} isLoading={isLoading}>
              Login
            </Button>
            <CustomButton variant="link" className="text-13 text-text-primary !p-1 " onClick={() => setView({ value: "TRY_OTHER_OPTIONS" })}>
              Try other options
            </CustomButton>
          </>
        );
    }
  };

  return (
    <LoginLayout sideBanner={loginDefaultBanner}>
      <div className=" flex flex-col items-center w-full pb-24 md:pb-0 md:max-w-[700px]">
        <div className="mt-12 h-fit md:w-[500px] shadow-form rounded-llg p-5 flex flex-col items-center">{renderContent()}</div>
      </div>
    </LoginLayout>
  );
};

export default MfaValidatePage;
